import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  avaliarOperacaoNormativa,
  type BloqueioOperacional,
  type EtapaWorkflowOperacao,
  type OperacaoNormativaContext,
  type ResultadoConformidadeOperacao,
} from '../domain/operationRequirementsEngine'
import type {
  ObrigacaoOperacional,
  ParticipanteOperacao,
  TipoObrigacaoOperacional,
} from '../domain/operacaoParticipantes'

export type OperationRuntimeState = {
  operacao: OperacaoNormativaContext | null
  participanteAtual: ParticipanteOperacao | null
  resultado: ResultadoConformidadeOperacao | null
  etapaAtual: EtapaWorkflowOperacao | null
  loading: boolean
  error: string | null
}

export type OperationRuntimeActions = {
  setOperacaoAtual: (operacao: OperacaoNormativaContext | null) => void
  setParticipanteAtual: (participante: ParticipanteOperacao | null) => void
  reavaliarOperacao: () => ResultadoConformidadeOperacao | null
  limparOperacao: () => void
  getObrigacoesPendentesDoParticipante: (participanteId?: string | null) => ObrigacaoOperacional[]
  getObrigacoesPorTipo: (tipo: TipoObrigacaoOperacional) => ObrigacaoOperacional[]
  getBloqueiosCriticos: () => BloqueioOperacional[]
  podeIniciarOperacao: () => boolean
}

type OperationContextValue = OperationRuntimeState & OperationRuntimeActions

const OperationContext = createContext<OperationContextValue | null>(null)
const loading = false

function avaliarComSeguranca(operacao: OperacaoNormativaContext | null) {
  if (!operacao) return { resultado: null, error: null }

  try {
    return {
      resultado: avaliarOperacaoNormativa(operacao),
      error: null,
    }
  } catch (err) {
    return {
      resultado: null,
      error: err instanceof Error ? err.message : 'Erro ao avaliar operação.',
    }
  }
}

export function OperationProvider({ children }: { children: ReactNode }) {
  const [operacao, setOperacao] = useState<OperacaoNormativaContext | null>(null)
  const [participanteAtual, setParticipante] = useState<ParticipanteOperacao | null>(null)
  const [resultado, setResultado] = useState<ResultadoConformidadeOperacao | null>(null)
  const [error, setError] = useState<string | null>(null)

  const etapaAtual = resultado?.etapaSugerida ?? null

  const setOperacaoAtual = useCallback((nextOperacao: OperacaoNormativaContext | null) => {
    const avaliacao = avaliarComSeguranca(nextOperacao)
    setOperacao(nextOperacao)
    setResultado(avaliacao.resultado)
    setError(avaliacao.error)
    if (!nextOperacao) setParticipante(null)
  }, [])

  const setParticipanteAtual = useCallback((participante: ParticipanteOperacao | null) => {
    setParticipante(participante)
  }, [])

  const reavaliarOperacao = useCallback(() => {
    const avaliacao = avaliarComSeguranca(operacao)
    setResultado(avaliacao.resultado)
    setError(avaliacao.error)
    return avaliacao.resultado
  }, [operacao])

  const limparOperacao = useCallback(() => {
    setOperacao(null)
    setParticipante(null)
    setResultado(null)
    setError(null)
  }, [])

  const getObrigacoesPendentesDoParticipante = useCallback((participanteId?: string | null) => {
    const targetId = participanteId ?? participanteAtual?.id
    return targetId && resultado
      ? resultado.obrigacoes.filter(item => item.participanteId === targetId)
      : []
  }, [participanteAtual?.id, resultado])

  const getObrigacoesPorTipo = useCallback((tipo: TipoObrigacaoOperacional) => {
    return resultado?.obrigacoes.filter(item => item.tipo === tipo) ?? []
  }, [resultado])

  const getBloqueiosCriticos = useCallback(() => {
    return resultado?.bloqueios.filter(item => item.severidade === 'bloqueante') ?? []
  }, [resultado])

  const podeIniciarOperacao = useCallback(() => !!resultado?.podeIniciar && !error, [error, resultado?.podeIniciar])

  const value = useMemo<OperationContextValue>(() => ({
    operacao,
    participanteAtual,
    resultado,
    etapaAtual,
    loading,
    error,
    setOperacaoAtual,
    setParticipanteAtual,
    reavaliarOperacao,
    limparOperacao,
    getObrigacoesPendentesDoParticipante,
    getObrigacoesPorTipo,
    getBloqueiosCriticos,
    podeIniciarOperacao,
  }), [
    operacao,
    participanteAtual,
    resultado,
    etapaAtual,
    error,
    setOperacaoAtual,
    setParticipanteAtual,
    reavaliarOperacao,
    limparOperacao,
    getObrigacoesPendentesDoParticipante,
    getObrigacoesPorTipo,
    getBloqueiosCriticos,
    podeIniciarOperacao,
  ])

  return (
    <OperationContext.Provider value={value}>
      {children}
    </OperationContext.Provider>
  )
}

export function useOperation() {
  const ctx = useContext(OperationContext)
  if (!ctx) throw new Error('useOperation deve ser usado dentro de <OperationProvider>')
  return ctx
}
